/** The order POST began, but the client did not receive authoritative evidence
 * that the venue accepted or rejected it. This is intentionally not a normal
 * rejection: retrying can duplicate live exposure. */
export class OrderSubmissionUnknownError extends Error {
  readonly code = 'KALSHI_ORDER_STATE_UNKNOWN';

  constructor(message = 'Order result is unknown. Check Open Orders and receipts before trying again.') {
    super(message);
    this.name = 'OrderSubmissionUnknownError';
  }
}

/**
 * #1229 — Kalshi self-trade prevention cancelled the order at placement.
 *
 * Every G1 Kalshi order is sent `taker_at_cross`: when it would trade against
 * a resting order from the same account (the desk's, under org keys), the
 * venue cancels the INCOMING order's unfilled remainder and still answers
 * 201. Contracts matched before the cross are kept. The relay classifies the
 * response as `g1Outcome: { kind: 'cancelled_self_trade', filled, requested }`
 * beside the untouched venue body.
 */
export interface SelfTradeCancel {
  /** Contracts matched before the cross stopped the order. */
  filled: number;
  /** Contracts the order asked for. */
  requested: number;
}

/** Read the relay's classification off a placement response (any nesting). */
export function readSelfTradeCancel(data: unknown): SelfTradeCancel | null {
  if (!data || typeof data !== 'object') return null;
  const outcome = (data as Record<string, unknown>).g1Outcome;
  if (!outcome || typeof outcome !== 'object') return null;
  const o = outcome as Record<string, unknown>;
  if (o.kind !== 'cancelled_self_trade') return null;
  const filled = typeof o.filled === 'number' && Number.isFinite(o.filled) && o.filled >= 0 ? o.filled : null;
  const requested = typeof o.requested === 'number' && Number.isFinite(o.requested) && o.requested > 0 ? o.requested : null;
  if (filled == null || requested == null) return null;
  return { filled, requested };
}

/** The line at the control when nothing filled. A complete sentence: a modal
 *  or result line prints it on its own, the one-click strip after the label. */
export const SELF_TRADE_CANCELLED_MESSAGE =
  'Not placed: cancelled by Kalshi self-trade protection; your desk likely has a resting order on the other side of this price.';

/** The amber line for a partial: the fills are real, nothing rests. */
export function selfTradePartialMessage(cancel: SelfTradeCancel): string {
  return `Filled ${cancel.filled} of ${cancel.requested}; remainder cancelled by self-trade protection`;
}

/** Tooltip reference beside the line. Never the only copy of the state. */
export const SELF_TRADE_CANCEL_REFERENCE =
  'Cancelled at placement by Kalshi, normally self-trade protection (taker_at_cross): an ' +
  'order that would trade against a resting order on the same account is cancelled rather ' +
  'than matched; contracts matched before the cross are kept. Check the desk\'s open orders ' +
  'on the other side of this price, pull or edit them, or place at a price that does not cross.';

/** A definite non-placement: the venue answered, nothing rests, nothing filled.
 *  Rings the rejection tone family; safe to retry once the other side is
 *  pulled or the price no longer crosses it. */
export class SelfTradeCancelledError extends Error {
  readonly code = 'KALSHI_SELF_TRADE_CANCELLED';
  readonly filled: number;
  readonly requested: number;

  constructor(cancel: SelfTradeCancel) {
    super(SELF_TRADE_CANCELLED_MESSAGE);
    this.name = 'SelfTradeCancelledError';
    this.filled = cancel.filled;
    this.requested = cancel.requested;
  }
}
